import React, { ReactNode } from 'react'
import { ImageSourcePropType, Linking } from 'react-native'
import { AntDesign, Entypo, FontAwesome, Ionicons } from '@expo/vector-icons'
import { ListingStatus, TokenType } from '@vatom/sdk/core'
import { TxKeyPath } from '@vatom/utils'
import { Text, theme } from '@vatom/wombo'
import { TranslateOptions } from 'i18n-js'
import { Actionsheet, Icon, Image, useColorModeValue } from 'native-base'

import logger from '../../../logger'

export interface ActionSheetProps {
  setOpen: (b: boolean) => void
  token: TokenType
  isOpen: boolean
  label?: ReactNode
  color?: string
  iconSource?: ImageSourcePropType
  sellAction?: () => void
  shareAction?: () => void
  shareLinkAction?: () => void
  splitAction?: () => void
  dropAction?: () => void
  pickupAction?: () => void
  mintAction?: () => void
  infoAction?: () => void
  deleteAction?: () => void
  sendAction?: () => void
  unSellAction?: () => void
  maxWidth?: number | string
}

export interface ActionInterface {
  iconType: string
  name: string
  iconSource?: typeof AntDesign | typeof Entypo | typeof FontAwesome | typeof Ionicons
  imageSource?: ImageSourcePropType
  title: string
  color: string
  type: string
  onPress?: () => void
  tx?: TxKeyPath
  txOptions?: TranslateOptions
}
export type ActionInterfaceArray = ActionInterface[]

export const ActionSheetFoundation = ({
  setOpen,
  isOpen,
  sellAction,
  shareAction,
  shareLinkAction,
  splitAction,
  dropAction,
  pickupAction,
  mintAction,
  infoAction,
  deleteAction,
  sendAction,
  unSellAction,
  token,
  maxWidth = '100%'
}: ActionSheetProps) => {
  logger.info('ActionSheetFoundation')

  const colorScheme = {
    light: {
      base: theme.colors.textLightMode[600],
      danger: theme.colors.systemColorsLight.red
    },
    dark: {
      base: theme.colors.textDarkMode[300],
      danger: theme.colors.systemColorsDark.red
    }
  }
  const styles = useColorModeValue(colorScheme.light, colorScheme.dark)

  const canPerformAction = (token: TokenType, name: string) => {
    return !!token.actions.find(a => a === name)
  }

  const options: ActionInterfaceArray = [
    {
      iconType: 'icon',
      name: 'forward',
      iconSource: Entypo,
      title: 'Share',
      color: styles.base,
      onPress: shareAction,
      type: 'Share',
      tx: 'common.share'
    },
    {
      iconType: 'icon',
      name: 'forward',
      iconSource: Entypo,
      title: 'Share as a Link',
      color: styles.base,
      onPress: shareLinkAction,
      type: 'ShareLink',
      tx: 'actionSheets.shareLink'
    },
    {
      iconType: 'image',
      name: 'icon-drop',
      imageSource: require('./assets/map-marked-alt.png'),
      title: 'Drop',
      color: styles.base,
      onPress: dropAction,
      type: 'Drop',
      tx: 'common.drop'
    },
    {
      iconType: 'icon',
      name: 'map-marker',
      iconSource: FontAwesome,
      title: 'Pickup',
      color: styles.base,
      onPress: pickupAction,
      type: 'Pickup',
      tx: 'common.pickup'
    },
    {
      iconType: 'icon',
      name: 'credit',
      iconSource: Entypo,
      title: 'Sell',
      color: styles.base,
      onPress: sellAction,
      type: 'List',
      tx: 'common.sell'
    },
    {
      iconType: 'icon',
      name: 'block',
      iconSource: Entypo,
      title: 'Remove From Sale',
      color: styles.base,
      onPress: unSellAction,
      type: 'Delist',
      tx: 'actionSheets.removeSale'
    },
    {
      iconType: 'icon',
      name: 'paper-plane-o',
      iconSource: FontAwesome,
      title: 'Send',
      color: styles.base,
      onPress: sendAction,
      type: 'Transfer',
      tx: 'common.send'
    }
  ]

  const actionsArray: ActionInterfaceArray | null = []
  for (const action of options) {
    // Hack for Tide and State Farm
    const hack =
      action.type === 'ShareLink' &&
      (token.studioInfo?.campaignId === 'NxY6V4ZkZH' ||
        token.studioInfo?.campaignId === 'ZVrhQOIIsR')
    const canPerform: boolean = !hack && canPerformAction(token, action.type)

    if (canPerform) {
      actionsArray.push(action)
    }
  }

  if (token.blockchainInfo && !token.blockchainInfo.tokenLink) {
    const name = token.blockchainInfo.network
    const badge = token.getNetworkIcon(name)
    actionsArray.push({
      iconType: 'image',
      imageSource: badge,
      name: 'Network',
      iconSource: AntDesign,
      title: `Mint on ${token.blockchainInfo.networkName}`,
      color: styles.base,
      onPress: mintAction,
      type: 'MintNFT',
      tx: 'actionSheets.mintOn',
      txOptions: {
        blockchain: token.blockchainInfo.networkName
      }
    })
  }

  if (token.commerceInfo?.status === ListingStatus.Active) {
    actionsArray.push({
      iconType: 'icon',
      name: 'external-link',
      iconSource: FontAwesome,
      title: `View on Marketplace`,
      color: styles.base,
      onPress: () =>
        token.commerceInfo?.productUrl && Linking.openURL(token.commerceInfo?.productUrl),
      type: 'MintNFT',
      tx: 'actionSheets.viewMarketplace'
    })
  }

  actionsArray.push({
    iconType: 'icon',
    name: 'infocirlceo',
    iconSource: AntDesign,
    title: 'General Info',
    color: styles.base,
    onPress: infoAction,
    type: 'info',
    tx: 'actionSheets.generalInfo'
  })

  if (token.studioInfo?.businessId !== 'PoIvoqVCm3' && token.type === 'vatom') {
    actionsArray.push({
      iconType: 'icon',
      name: 'trash-outline',
      iconSource: Ionicons,
      title: 'Delete',
      color: styles.danger,
      onPress: deleteAction,
      type: 'Delete',
      tx: 'common.delete'
    })
  }

  const actionOptions = actionsArray

  return (
    <Actionsheet
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      maxWidth={maxWidth}
      margin={'auto'}
      useRNModal={true}
      // @ts-ignore prop doesn't exit on types
      _backdropFade={{
        entryDelay: 150,
        exitDelay: 10
      }}
    >
      <Actionsheet.Content
        _light={{
          backgroundColor: theme.colors.white
        }}
        _dark={{
          backgroundColor: theme.colors.grayDarkMode[900]
        }}
      >
        {actionOptions.map((item, index) => {
          return (
            <Actionsheet.Item
              _light={{
                backgroundColor: theme.colors.white
              }}
              _dark={{
                backgroundColor: theme.colors.grayDarkMode[900]
              }}
              onPress={item?.onPress ?? null}
              key={`foundation-${index}`}
              startIcon={
                item.iconType === 'image' ? (
                  <Image
                    tintColor={item.color}
                    accessibilityIgnoresInvertColors={true}
                    size="20px"
                    resizeMode="contain"
                    source={item.imageSource}
                    alt="actions"
                  />
                ) : (
                  <Icon
                    as={item.iconSource}
                    size="20px"
                    color={item.color}
                    name={item.name}
                    alignSelf="center"
                  />
                )
              }
              _hover={{
                bg: 'gray.100:alpha.20'
              }}
              _pressed={{
                bg: 'gray.100:alpha.20'
              }}
            >
              <Text
                fontSize={15}
                fontWeight="normal"
                color={item.color}
                tx={item.tx}
                txOptions={item.txOptions}
              >
                {item.title}
              </Text>
            </Actionsheet.Item>
          )
        })}
      </Actionsheet.Content>
    </Actionsheet>
  )
}

export default ActionSheetFoundation
